import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { translate } from 'translations/i18n';

import { ButtonIcon, Menu, MenuItem } from '@companycam/slab-web';
import useCompanyBillingStatus from 'components/billing/useCompanyBillingStatus';
import CCTooltip from 'components/shared/DesignSystem/CCFloating/CCTooltip';
import { canAccessFeature, FEATURES } from 'helpers/features';
import useShowTaskListTemplateTooltip from 'components/projects/taskList/hooks/useShowTaskListTemplateTooltip';

import UpgradeIcon from 'components/shared/DesignSystem/UpgradeIcon';

const TooltipText = styled.p`
  margin-bottom: 0px;
  width: 240px;
  font-weight: 500;
  font-size: var(--cc_size_text_xs);
`;

const TaskListHeaderMenu = ({
  isVisible,
  showEditOptions,
  showReorderSectionsOption,
  showReorderTasksOption,
  showTemplateOption,
  showDeleteOption,
  onEditClick,
  onExportClick,
  onReorderSectionsClick,
  onReorderTasksClick,
  onSaveAsTemplateClick,
  onDeleteClick,
  taskListName,
}) => {
  const { showTaskListTemplateTooltip, onTooltipClose } =
    useShowTaskListTemplateTooltip();

  const { featureSet, loading } = useCompanyBillingStatus();
  const planIncludesTemplates = canAccessFeature(
    featureSet,
    FEATURES.TEMPLATES,
  );

  const templateOptionStyle = !planIncludesTemplates
    ? {
        opacity: 0.4,
        pointerEvents: 'none',
      }
    : null;

  if (!isVisible) {
    return null;
  }
  return (
    <div>
      <CCTooltip
        position="bottom-end"
        offset={50}
        triggerStyle={
          showTaskListTemplateTooltip && !loading ? 'always' : 'hidden'
        }
        onClickOutside={onTooltipClose}
      >
        <TooltipText>
          {planIncludesTemplates
            ? translate('projects.taskList.taskListHeader.tooltipMsg')
            : translate('projects.taskList.taskListHeader.tooltipUpgradeMsg')}
        </TooltipText>
      </CCTooltip>
      <Menu
        triggerButton={
          <ButtonIcon
            accessibilityLabel={translate(
              'projects.taskList.taskListHeader.manageTaskList',
              { taskListName },
            )}
            data-testid="project__item__more-menu-trigger"
            iconName="dots-horizontal"
            shape="square"
          />
        }
      >
        {showEditOptions ? (
          <MenuItem onClick={onEditClick} iconName="pencil-outline">
            {translate('projects.taskList.taskListHeader.editTaskList')}
          </MenuItem>
        ) : null}

        <MenuItem iconName="file-pdf-box" onClick={onExportClick}>
          {translate('projects.taskList.taskListHeader.exportToPdf')}
        </MenuItem>

        {showReorderSectionsOption ? (
          <MenuItem iconName="sort" onClick={onReorderSectionsClick}>
            {translate('projects.taskList.taskListSection.reorderSections')}
          </MenuItem>
        ) : null}

        {showReorderTasksOption ? (
          <MenuItem iconName="sort" onClick={onReorderTasksClick}>
            {translate('projects.taskList.taskListHeader.reorderTasks')}
          </MenuItem>
        ) : null}
        {showTemplateOption ? (
          <MenuItem
            contentAfter={
              !planIncludesTemplates ? (
                <UpgradeIcon
                  size="medium"
                  style={{ marginLeft: 'auto', opacity: 1 }}
                />
              ) : null
            }
            iconName="text-box-plus-outline"
            onClick={onSaveAsTemplateClick}
            disabled={!planIncludesTemplates}
            style={templateOptionStyle}
          >
            {translate('projects.taskList.taskListHeader.saveAsTemplate')}
          </MenuItem>
        ) : null}
        {showDeleteOption ? (
          <>
            <hr />
            <MenuItem iconName="delete-outline" onClick={onDeleteClick}>
              {translate('projects.taskList.taskListHeader.deleteTaskList')}
            </MenuItem>
          </>
        ) : null}
      </Menu>
    </div>
  );
};

TaskListHeaderMenu.propTypes = {
  isVisible: PropTypes.bool,
  showEditOptions: PropTypes.bool,
  showReorderTasksOption: PropTypes.bool,
  showReorderSectionsOption: PropTypes.bool,
  showTemplateOption: PropTypes.bool,
  showDeleteOption: PropTypes.bool,
  onEditClick: PropTypes.func,
  onExportClick: PropTypes.func,
  onReorderSectionsClick: PropTypes.func,
  onReorderTasksClick: PropTypes.func,
  onSaveAsTemplateClick: PropTypes.func,
  onDeleteClick: PropTypes.func,
  taskListName: PropTypes.string,
};

export default TaskListHeaderMenu;
